/**
 * MessageList Organism Component
 *
 * Renders a list of conversation messages with loading states,
 * thinking indicators, and proper spacing.
 */

import React from 'react';
import type { Message, QuestionnaireType } from '@ideoz/core';
import { MessageItem } from '../molecules/MessageItem';
import ChatThinking from '../molecules/chat-thinking';

// ============================================================================
// TYPES
// ============================================================================

export interface MessageListProps {
  conversationId?: string | null;
  projectId?: string | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  isClarification: boolean;
  isReflection: boolean;
  isRepeated: boolean;
  isReadyToCreate: boolean;
  canBeDrawn: QuestionnaireType | null;
  readyToGenerate: string | null;
  showBannerPatient: boolean;
  isNeedTime: boolean;
  isCreatingDrawing: boolean;
  showCreateButton: boolean;
  shouldShowBanner: boolean;
  isWeb: boolean;
  isLoggedIn: boolean;
  isCreatingAccount?: boolean;
  isWaitingForDrawingCreation?: boolean;
  isPrototyper?: boolean;
  onPrototypeReturn?: () => void;
  onPrototyperClick?: (prototyperDemoLinkId?: string | number) => void;
  forcedDirection?: 'rtl' | 'ltr';

  // Actions
  onSubmit: (text: string) => void;
  onActionClick?: (action: string, message?: string) => void;
  onFeedback?: (
    type: 'like' | 'dislike',
    messageId?: string | null,
    projectId?: string | null
  ) => void | Promise<void>;
  setIsReflection: (value: boolean) => void;
  setIsRepeated: (value: boolean) => void;
  onRetryDrawing?: (drawType: string) => void;
  isPrototypeOpen?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export const MessageList: React.FC<MessageListProps> = ({
  conversationId,
  projectId,
  messages,
  isLoading,
  isStreaming,
  isClarification,
  isReflection,
  isRepeated,
  isReadyToCreate,
  canBeDrawn,
  readyToGenerate,
  showBannerPatient,
  isNeedTime,
  isCreatingDrawing,
  showCreateButton,
  shouldShowBanner,
  isWeb,
  isLoggedIn,
  isCreatingAccount = false,
  isWaitingForDrawingCreation = false,
  onPrototypeReturn,
  onPrototyperClick,
  onSubmit,
  onActionClick = () => {},
  onFeedback,
  setIsReflection,
  setIsRepeated,
  onRetryDrawing,
  forcedDirection,
  isPrototypeOpen = false,
  isPrototyper = false,
}) => {
  // ========== Render Helpers ==========

  /**
   * Calculate minimum height for last message
   */
  const getLastMessageMinHeight = (index: number): string => {
    if (index !== messages.length - 1) return '';

    if (isWeb) {
      return shouldShowBanner && !isReadyToCreate
        ? 'min-h-[calc(100vh-360px)]'
        : 'min-h-[calc(100vh-300px)]';
    } else {
      return shouldShowBanner && !isReadyToCreate
        ? 'min-h-[calc(100vh-272px)]'
        : 'min-h-[calc(100vh-227px)]';
    }
  };

  /**
   * Render thinking indicator for last message (before message)
   */
  const renderThinkingIndicatorBefore = (index: number) => {
    const isLastMessage = index === messages.length - 1;
    if (!isLastMessage) return null;

    // Show thinking during loading
    if (isLoading) {
      const thinkingContent =
        !isWeb && canBeDrawn && isCreatingDrawing
          ? `Generating ${canBeDrawn.replace(/_/g, ' ')}`
          : undefined;

      return <ChatThinking messages={messages} content={thinkingContent} />;
    }

    return null;
  };

  /**
   * Render thinking indicator for last message (after message)
   */
  const renderThinkingIndicatorAfter = (index: number) => {
    const isLastMessage = index === messages.length - 1;
    if (!isLastMessage) return null;

    // Show thinking for plugin drawing creation (after message)
    if (!isWeb && (isWaitingForDrawingCreation || (canBeDrawn && isCreatingDrawing))) {
      return (
        <ChatThinking
          messages={messages}
          className="pb-4"
          content={canBeDrawn ? `Generating ${canBeDrawn.replace(/_/g, ' ')}` : undefined}
        />
      );
    }

    return null;
  };

  // ========== Render ==========

  return (
    <>
      {messages.map((message, index) => {
        const isLastMessage = index === messages.length - 1;

        return (
          // <article key={message.id || index} className={`pb-6 ${getLastMessageMinHeight(index)}`}>
          <article key={message.id || index} className={`pb-6 ${isPrototypeOpen ? 'text-sm' : ''}`}>
            {/* Thinking indicator before message */}
            {renderThinkingIndicatorBefore(index)}

            {/* Message */}
            <MessageItem
              message={message}
              conversationId={conversationId}
              projectId={projectId}
              isLastMessage={isLastMessage}
              isReflection={isReflection}
              isRepeated={isRepeated}
              isClarification={isClarification}
              showCreateButton={showCreateButton}
              shouldShowBanner={shouldShowBanner}
              isReadyToCreate={isReadyToCreate}
              showBannerPatient={showBannerPatient}
              isNeedTime={isNeedTime}
              canBeDrawn={canBeDrawn}
              readyToGenerate={readyToGenerate}
              isCreatingAccount={isCreatingAccount}
              isStreaming={isStreaming}
              isLoading={isLoading}
              isWeb={isWeb}
              isLoggedIn={isLoggedIn}
              onPrototyperClick={onPrototyperClick}
              onSubmit={onSubmit}
              onActionClick={onActionClick}
              onFeedback={onFeedback}
              setIsReflection={setIsReflection}
              setIsRepeated={setIsRepeated}
              onRetryDrawing={onRetryDrawing}
              forcedDirection={forcedDirection}
              isPrototypeOpen={isPrototypeOpen}
              isPrototyper={isPrototyper}
            />

            {/* Thinking indicator after message */}
            {renderThinkingIndicatorAfter(index)}
          </article>
        );
      })}
    </>
  );
};

export default MessageList;
